import { GDQEventGame, GDQLeaderboardEntry, GDQCategoryType } from '../types';
import { DEFAULT_PRESETS } from './presets';

// Local storage keys
const STORAGE_KEYS = {
  CURRENT_GAME_ID: 'gdq_current_game_id',
  CUSTOM_TEMPLATES: 'gdq_custom_templates',
  CUSTOM_GAMES: 'gdq_custom_games',
  LEADERBOARD: 'gdq_leaderboard',
  SYSTEM_INIT: 'gdq_system_init'
};

/**
 * Bootstraps the local database with preloaded templates if empty
 */
export function initializeDB(): void {
  const isInit = localStorage.getItem(STORAGE_KEYS.SYSTEM_INIT);
  if (!isInit) {
    // Write out preloaded presets as custom templates
    localStorage.setItem(STORAGE_KEYS.CUSTOM_TEMPLATES, JSON.stringify(DEFAULT_PRESETS));
    // Default current game is the Marriage Anniversary preset
    localStorage.setItem(STORAGE_KEYS.CURRENT_GAME_ID, DEFAULT_PRESETS[0].id);
    // Mark as initialized
    localStorage.setItem(STORAGE_KEYS.SYSTEM_INIT, 'true');
    console.log('GDQ Local Database Initialized successfully.');
  }
}

/**
 * Gets the ID of the current active event game
 */
export function getCurrentGameId(): string | null {
  return localStorage.getItem(STORAGE_KEYS.CURRENT_GAME_ID);
}

/**
 * Sets the active event game ID
 */
export function setCurrentGameId(gameId: string | null): void {
  if (gameId) {
    localStorage.setItem(STORAGE_KEYS.CURRENT_GAME_ID, gameId);
  } else {
    localStorage.removeItem(STORAGE_KEYS.CURRENT_GAME_ID);
  }
}

/**
 * Retrieves all templates (preloaded presets + custom templates compiled by GMs)
 */
export function getTemplates(): GDQEventGame[] {
  initializeDB();
  const raw = localStorage.getItem(STORAGE_KEYS.CUSTOM_TEMPLATES);
  if (!raw) return DEFAULT_PRESETS;
  try {
    const custom = JSON.parse(raw) as GDQEventGame[];
    // Ensure default presets are always present
    const combined = [...custom];
    DEFAULT_PRESETS.forEach(pre => {
      if (!combined.some(c => c.id === pre.id)) {
        combined.unshift(pre);
      }
    });
    return combined;
  } catch {
    return DEFAULT_PRESETS;
  }
}

/**
 * Saves a game as a reusable template
 */
export function saveTemplate(game: GDQEventGame): void {
  const templates = getTemplates();
  // Filter out existing template with same ID
  const newTemplates = templates.filter(t => t.id !== game.id);
  newTemplates.push({ ...game, isTemplate: true });
  localStorage.setItem(STORAGE_KEYS.CUSTOM_TEMPLATES, JSON.stringify(newTemplates));
}

/**
 * Deletes a template
 */
export function deleteTemplate(templateId: string): void {
  const templates = getTemplates();
  const filtered = templates.filter(t => t.id !== templateId);
  localStorage.setItem(STORAGE_KEYS.CUSTOM_TEMPLATES, JSON.stringify(filtered));
}

/**
 * Retrieves all custom loaded event games
 */
export function getEventGames(): GDQEventGame[] {
  initializeDB();
  // We can treat our templates and running games unified or separate
  const rawGames = localStorage.getItem(STORAGE_KEYS.CUSTOM_GAMES);
  const templates = getTemplates();
  
  let games: GDQEventGame[] = [];
  if (rawGames) {
    try {
      games = JSON.parse(rawGames) as GDQEventGame[];
    } catch {
      games = [];
    }
  }
  // Any template can also be opened/used as a running game, so combine them
  const combined = [...games];
  templates.forEach(tpl => {
    if (!combined.some(g => g.id === tpl.id)) {
      combined.push(tpl);
    }
  });

  return combined;
}

/**
 * Saves or updates an active event game
 */
export function saveEventGame(game: GDQEventGame): void {
  const games = getEventGames().filter(g => g.id !== game.id && !g.isTemplate);
  games.push(game);
  localStorage.setItem(STORAGE_KEYS.CUSTOM_GAMES, JSON.stringify(games));
}

/**
 * Gets a specific event game
 */
export function getEventGameById(id: string): GDQEventGame | null {
  const games = getEventGames();
  return games.find(g => g.id === id) || null;
}

/**
 * Retrieves the currently active game object
 */
export function getCurrentEventGame(): GDQEventGame | null {
  const currentId = getCurrentGameId();
  if (!currentId) return null;
  return getEventGameById(currentId);
}

/**
 * Leaderboard & Scoring Log entries
 */
export function getLeaderboard(): GDQLeaderboardEntry[] {
  const raw = localStorage.getItem(STORAGE_KEYS.LEADERBOARD);
  if (!raw) return [];
  try {
    return JSON.parse(raw) as GDQLeaderboardEntry[];
  } catch {
    return [];
  }
}

/**
 * Logs a player's puzzle solution onto the tournament leaderboard
 */
export function addLeaderboardEntry(entry: Omit<GDQLeaderboardEntry, 'id' | 'timestamp'>): GDQLeaderboardEntry {
  const leaderboard = getLeaderboard();
  const fullEntry: GDQLeaderboardEntry = {
    ...entry,
    id: 'score_' + Date.now() + '_' + Math.random().toString(36).substr(2, 5),
    timestamp: new Date().toLocaleString()
  };
  leaderboard.push(fullEntry);
  localStorage.setItem(STORAGE_KEYS.LEADERBOARD, JSON.stringify(leaderboard));
  return fullEntry;
}

/**
 * Resets/Clears the leaderboard scores for a game/event
 */
export function clearLeaderboard(gameId?: string): void {
  if (gameId) {
    const leaderboard = getLeaderboard().filter(e => e.gameId !== gameId);
    localStorage.setItem(STORAGE_KEYS.LEADERBOARD, JSON.stringify(leaderboard));
  } else {
    localStorage.setItem(STORAGE_KEYS.LEADERBOARD, JSON.stringify([]));
  }
}

/**
 * Calculates current player's rank relative to matches on the same puzzle
 */
export function getPuzzleRanking(puzzleId: string, playerScore: number): { rank: number; total: number } {
  const scores = getLeaderboard()
    .filter(e => e.puzzleId === puzzleId)
    .sort((a, b) => b.score - a.score); // highest score is first rank!
  
  if (scores.length === 0) return { rank: 1, total: 1 };
  
  // Find ranking
  const idx = scores.findIndex(e => e.score <= playerScore);
  const rank = idx === -1 ? scores.length + 1 : idx + 1;
  return { rank, total: scores.length + 1 };
}
